import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { createSelector } from "@reduxjs/toolkit";

import makeAjaxRequest from "helpers/makeAjaxRequest";
import { rootUrl } from "../actions/constants";

const flattenedArray = (arr, key) =>
  arr.reduce((acc, item) => {
    const { [key]: _, ...itemWithoutKey } = item;
    return acc.concat(item[key].map(innerItem => ({ ...itemWithoutKey, ...innerItem })));
  }, []);

const selectTable = state => state.rums;

export const selectedOperationSelector = createSelector(
  [selectTable],
  rums => rums.selectedOperation || {}
);
export const operationRosterDataSelector = createSelector(
  [selectTable, (state, operationCode) => operationCode],
  (rums, operationCode) => {
    const matchingData = rums.rosterData?.find(item => item.key === operationCode);
    return matchingData ? matchingData.value : [];
  }
);
export const selectedOwnerRow = createSelector(
  [selectTable],
  rums => rums.selectedOwnerRow || {}
);
export const selectMainGrid = createSelector(
  [selectTable],
  rums => rums.main || {}
);
export const selectPersonRoleLov = createSelector(
  [selectTable],
  rums => rums.personRoleLov || []
);

export const fetchRumsData = createAsyncThunk(
  "rums/browse",
  async (_, { rejectWithValue }) => {
    try {
      const response = await makeAjaxRequest.postWithAuth(`${rootUrl}/admin/rums/browse`, {});
      const mainData = response.data.map(({ operRosterDto, ...rest }) => rest);
      const rosterData = response.data.map((data) => ({
        key: data.operationCode,
        value: data.operRosterDto || []
      }));
      return {
        mainData,
        rosterData
      };
    } catch (err) {
      return rejectWithValue(err.response ? err.response.data : err.message);
    }
  }
);

export const fetchRumsRosterData = createAsyncThunk(
  "rums/rosterData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await makeAjaxRequest.postWithAuth(`${rootUrl}/admin/rums/browse`, {});

      return response.data.map((data) => ({ key: data.operationCode, value: data.operRosterDto || [] }));
    } catch (err) {
      return rejectWithValue(err.response ? err.response.data : err.message);
    }
  }
);
export const fetchPersonRoleLov = createAsyncThunk(
  "rums/personrolelov",
  async (roster, { rejectWithValue }) => {
    try {
      const response = await makeAjaxRequest.fetchWithAuth(`${rootUrl}/admin/rums/${roster}/personrolelov`);
      return response?.map(({ key, value }) => ({ label: value, value, key }));
    } catch (err) {
      return rejectWithValue(err.response ? err.response.data : err.message);
    }
  }
);

const rumsSlice = createSlice({
  name: "rums",
  initialState: {
    selectedOperation: null,
    selectedOwnerRow: null,
    rosterData: [],
    main: {
      data: [],
      loading: false,
      error: null
    },
    personRoleLov: null
  },
  reducers: {
    saveSelectedSetupRow: (state, action) => {
      state.selectedOperation = action.payload;
    },
    saveSelectedOwnerRow: (state, action) => {
      const row = action.payload;
      const formattedRow = {
        ...row,
        managedBy: row?.managedByDto ? flattenedArray(row.managedByDto, "personRoleEnumDto") : [],
        memberRole: row?.memberRoleDto ? flattenedArray(row?.memberRoleDto, "rosterMreDto") : []
      };
      state.selectedOwnerRow = formattedRow;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchRumsData.pending, (state) => {
      state.main.loading = true;
    })
      .addCase(fetchRumsData.fulfilled, (state, action) => {
        state.main.loading = false;
        state.main.data = action.payload.mainData;
        state.rosterData = action.payload.rosterData;
      })
      .addCase(fetchRumsData.rejected, (state, action) => {
        state.main.loading = false;
        state.main.error = action.error.message;
      });
    builder.addCase(fetchRumsRosterData.fulfilled, (state, action) => {
      state.rosterData = action.payload;
    });
    builder.addCase(fetchPersonRoleLov.fulfilled, (state, action) => {
      state.personRoleLov = action.payload;
    });
  }
});

export const { saveSelectedSetupRow, saveSelectedOwnerRow } = rumsSlice.actions;

export default rumsSlice.reducer;
